import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
// styles
import {
  ColStyled,
  RowStyledHeading,
  RowStyledThreeItems,
  SectionContainerStyled,
  TypographyStyledHeading,
  TypographyStyledHeadingSubText,
} from './styles';

export const UnderstandingProductGroupingModule = ({
  backgroundVariant,
  cardDataArray,
  className,
  disableBottomPadding,
  headingSubText,
  headingSubTextAlignment,
  headingText,
  hideHeading,
  id,
  secondaryContent,
}) => {
  const theme = useContext(ThemeContext);
  const mediumUp = theme.mediumUp();
  const [isMediumUp, setIsMediumUp] = useState(mediumUp);

  useEffect(() => {
    if (mediumUp !== isMediumUp) {
      setIsMediumUp(mediumUp);
    }
  }, [mediumUp, isMediumUp]);

  const getCard = (item) => {
    return (
      <>
        <item.icon className='understanding-product-grouping-primary-icon' />
        <Typography
          component='h3'
          themeType={backgroundVariant === 'darkDefault' ? 'darkTheme' : 'lightTheme'}
          nextElement='typography'>
          {item.title}
        </Typography>
        {item.content}
      </>
    );
  };

  return (
    <SectionContainerStyled
      id={`${id}-understanding-product`}
      className={className}
      moduleName='understanding-product-module'
      backgroundVariant={backgroundVariant}
      themeType={backgroundVariant === 'darkDefault' ? 'darkTheme' : 'lightTheme'}
      moduleVariation='A'
      disableBottomPadding={disableBottomPadding}
      cardDataArray={cardDataArray}
      hideHeading={hideHeading}>
      <RowStyledHeading
        className='understanding-product-grouping-heading-row'
        align='center'
        hideHeading={hideHeading}>
        <Col medium={8}>
          <TypographyStyledHeading
            component='h2'
            themeType={backgroundVariant === 'darkDefault' ? 'darkTheme' : 'lightTheme'}
            align='center'
            cardDataArray={cardDataArray}
            hasHeadingSubtext={headingSubText}>
            {headingText}
          </TypographyStyledHeading>
          {headingSubText && (
            <TypographyStyledHeadingSubText
              nextElement='graphic'
              align={headingSubTextAlignment}
              cardDataArray={cardDataArray}>
              {headingSubText}
            </TypographyStyledHeadingSubText>
          )}
        </Col>
      </RowStyledHeading>
      {/* for 2 items */}
      {cardDataArray.length === 2 && (
        <Row
          className='understanding-product-card-container-row'
          align='center'>
          <Col large={10}>
            <Row>
              {cardDataArray.map((item) => (
                <ColStyled
                  key={`understanding-product-col-${item.id}`}
                  medium={6}>
                  {getCard(item)}
                </ColStyled>
              ))}
            </Row>
          </Col>
        </Row>
      )}

      {/* for 3 items */}
      {cardDataArray.length === 3 && (
        <RowStyledThreeItems className='understanding-product-card-container-row'>
          {cardDataArray.map((item) => (
            <ColStyled
              id={`understanding-product-element-${item.id}`}
              key={`understanding-product-col-${item.id}`}
              medium={6}
              large={4}>
              {getCard(item)}
            </ColStyled>
          ))}
        </RowStyledThreeItems>
      )}

      {/* for 4 items */}
      {cardDataArray.length === 4 && (
        <Row
          className='understanding-product-card-container-row'
          align='center'>
          {isMediumUp
            ? (
              cardDataArray.map((item) => (
                <ColStyled
                  key={`understanding-product-col-${item.id}`}
                  medium={6}
                  large={5}>
                  {getCard(item)}
                </ColStyled>
              ))
            ) : (
              <Col>
                <Accordion
                  id={`${id}-understanding-product-accordion`}
                  className='understanding-product-card-accordion'
                  firstItemOpen
                  items={cardDataArray} />
              </Col>
            )}
        </Row>
      )}

      {/* for 5 or more items */}
      {cardDataArray.length >= 5 && (
        <Row
          className='understanding-product-card-container-row'
          align='center'>
          {isMediumUp
            ? (
              cardDataArray.map((item) => (
                <ColStyled
                  key={`understanding-product-col-${item.id}`}
                  medium={6}
                  large={4}>
                  {getCard(item)}
                </ColStyled>
              ))
            ) : (
              <Col>
                <Accordion
                  id={`${id}-understanding-product-accordion`}
                  className='understanding-product-card-accordion'
                  firstItemOpen
                  items={cardDataArray} />
              </Col>
            )}
        </Row>
      )}

      {secondaryContent}
    </SectionContainerStyled>
  );
};

UnderstandingProductGroupingModule.propTypes = {
  /** background variant */
  backgroundVariant: PropTypes.string,
  /** card data array */
  cardDataArray: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.node.required,
      icon: PropTypes.node.required,
      title: PropTypes.node.required,
      content: PropTypes.node.required,
    }),
  ).isRequired,
  /** optional className */
  className: PropTypes.string,
  /** optional disableBottomPadding to remove bottom padding of the section */
  disableBottomPadding: PropTypes.bool,
  /** optional heading subtext */
  headingSubText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  /** optional boolean to hide the heading */
  hideHeading: PropTypes.bool,
  /** optional heading subtext alignment */
  headingSubTextAlignment: PropTypes.oneOf(['left', 'center', 'right']),
  /** heading text */
  headingText: PropTypes.node.isRequired,
  /** component id */
  id: PropTypes.string.isRequired,
  // ** optional block of secondary content */
  secondaryContent: PropTypes.node,
};

UnderstandingProductGroupingModule.defaultProps = {
  backgroundVariant: 'lightA',
  disableBottomPadding: false,
  headingSubTextAlignment: 'left',
};

export default UnderstandingProductGroupingModule;